import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import esLocale from '@fullcalendar/core/locales/es';

import { listUnits, updateUnit } from '../../../services/unitsApi';

import './vendor/core/index.css';
import './vendor/daygrid/index.css';
import './vendor/timegrid/index.css';
import './vendor/list/index.css';
import './CalendarSection.css';
import OperationalCalendar from './OperationalCalendar';

const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const isBrowser = typeof window !== 'undefined';

const EVENT_STATUS_ORDER = ['reserved', 'available', 'hold', 'maintenance', 'pending'];
const DEFAULT_EVENT_STATUS = 'available';

const STATUS_CANONICAL_MAP = {
  occupied: 'reserved',
  reserved: 'reserved',
  confirmed: 'reserved',
  accepted: 'reserved',
  booked: 'reserved',
  scheduled: 'reserved',
  hold: 'hold',
  blocked: 'hold',
  block: 'hold',
  maintenance: 'maintenance',
  service: 'maintenance',
  unavailable: 'maintenance',
  available: 'available',
  free: 'available',
  idle: 'available',
  pending: 'pending',
  request: 'pending',
  requested: 'pending',
  tentative: 'pending',
};

const STATUS_LABELS = {
  reserved: 'Reservada',
  available: 'Disponible',
  maintenance: 'Mantenimiento',
  hold: 'Bloqueo',
  pending: 'Pendiente',
};

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponible' },
  { value: 'reserved', label: 'Ocupada' },
  { value: 'hold', label: 'Bloqueo' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'pending', label: 'Pendiente' },
];

const OCCUPIED_STATUSES = new Set(['reserved']);
const BLOCKED_STATUSES = new Set(['maintenance', 'hold', 'pending']);
const DAY_CATEGORY_LABELS = {
  past: 'Dia pasado',
  free: 'Disponible',
  occupied: 'Ocupado',
  blocked: 'Bloqueado',
};
const DAY_NOTES_STORAGE_PREFIX = 'calendar-day-notes::';
const MANUAL_EVENTS_STORAGE_PREFIX = 'calendar-manual-events::';
const DAY_TASKS_STORAGE_PREFIX = 'calendar-day-checklist::';
const DEFAULT_PRIORITY = 'normal';
const PRIORITY_OPTIONS = [
  { value: 'high', label: 'Alta' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Baja' },
];
const PRIORITY_LABELS = {
  high: 'Alta prioridad',
  normal: 'Prioridad normal',
  low: 'Prioridad baja',
};

const CALENDAR_VIEW_OPTIONS = [
  { value: 'month', label: 'Mes' },
  { value: 'week', label: 'Semana' },
];

const CALENDAR_FILTER_GROUPS = [
  { key: 'available', label: 'Disponible', tone: 'available', statuses: ['available'] },
  { key: 'busy', label: 'Ocupado', tone: 'busy', statuses: ['reserved'] },
  { key: 'maintenance', label: 'Mantenimiento', tone: 'maintenance', statuses: ['maintenance', 'hold', 'pending'] },
];

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const createManualEventId = () => `manual-${Date.now()}-${Math.random()
  .toString(36)
  .slice(2, 8)}`;
const createTaskId = () => `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const TASK_PRESETS = ['Confirmar operadores', 'Validar mantenimiento', 'Enviar briefing'];

const createStatusCounters = () => {
  return EVENT_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});
};

const buildDefaultStatusFilters = () => {
  const initial = {};
  EVENT_STATUS_ORDER.forEach((status) => {
    initial[status] = true;
  });
  return initial;
};

const safeString = (value) => (typeof value === 'string' ? value : '');

function parseDateKey(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const asString = safeString(value).trim().slice(0, 10);
  if (!DATE_KEY_REGEX.test(asString)) return null;
  const [year, month, day] = asString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function ensureDateKey(value) {
  const date = parseDateKey(value);
  return formatDateKey(date);
}

function addDays(date, amount) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + amount);
  return next;
}

function addDaysIso(value, amount) {
  const date = parseDateKey(value);
  if (!date) return '';
  return formatDateKey(addDays(date, amount));
}

function clampRangeKeys(startKey, endKey) {
  const safeStart = ensureDateKey(startKey);
  const safeEnd = ensureDateKey(endKey || startKey);
  if (!safeStart) {
    return { start: '', end: '' };
  }
  if (!safeEnd || safeStart <= safeEnd) {
    return { start: safeStart, end: safeEnd || safeStart };
  }
  return { start: safeEnd, end: safeStart };
}

function normalizeStatus(value) {
  if (!value) return DEFAULT_EVENT_STATUS;
  const normalized = String(value).toLowerCase();
  if (STATUS_CANONICAL_MAP[normalized]) {
    return STATUS_CANONICAL_MAP[normalized];
  }
  return EVENT_STATUS_ORDER.includes(normalized) ? normalized : DEFAULT_EVENT_STATUS;
}

const compareDateKeys = (a, b) => a.localeCompare(b);

const listDateKeysInclusive = (startKey, endKey) => {
  const keys = [];
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey || startKey);
  if (!start || !end) return keys;
  const cursor = new Date(start.getTime());
  while (cursor.getTime() <= end.getTime()) {
    keys.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

const resolveDateKeyFromPoint = (view, startKey, endKey, clientX, clientY, fallbackKey) => {
  const rootEl = view?.el;
  if (!rootEl) return fallbackKey;
  const keys = listDateKeysInclusive(startKey, endKey);
  for (const key of keys) {
    const cell = rootEl.querySelector(`.fc-daygrid-day[data-date="${key}"]`);
    if (!cell) continue;
    const rect = cell.getBoundingClientRect();
    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return key;
    }
  }
  return fallbackKey;
};

const getRangeStartKey = (range) =>
  ensureDateKey(range.start) ||
  ensureDateKey(range.from) ||
  ensureDateKey(range.begin);

const getRangeEndKey = (range) =>
  ensureDateKey(range.end) ||
  ensureDateKey(range.to) ||
  ensureDateKey(range.hasta) ||
  ensureDateKey(range.finish) ||
  getRangeStartKey(range);

const cloneRangeWithBounds = (range, startKey, endKey) => {
  const next = { ...range };
  next.start = startKey;
  next.end = endKey;
  next.from = startKey;
  next.begin = startKey;
  next.hasta = endKey;
  next.finish = endKey;
  next.to = endKey;
  return next;
};

const resolveRangeStatus = (range, fallbackStatus) =>
  normalizeStatus(
    range?.status ||
      range?.unitStatus ||
      range?.state ||
      range?.type ||
      fallbackStatus
  );

function countInclusiveDays(startKey, endKey) {
  const start = parseDateKey(startKey);
  const end = parseDateKey(endKey || startKey);
  if (!start) return 0;
  if (!end) return 1;
  const diff = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  return diff >= 0 ? diff + 1 : 1;
}

function getUnitDisplayName(unit) {
  if (!unit) return 'Unidad';
  return (
    unit.economicNumber ||
    unit.model ||
    unit.tractorType ||
    `Unidad ${String(unit.id || '').slice(0, 5)}`
  );
}

const getStartOfWeek = (date) => {
  const base = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekDay = (base.getDay() + 6) % 7; // Monday as first day
  base.setDate(base.getDate() - weekDay);
  return base;
};

const getStartOfMonthGrid = (date) => {
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const weekDay = (firstOfMonth.getDay() + 6) % 7;
  firstOfMonth.setDate(firstOfMonth.getDate() - weekDay);
  return firstOfMonth;
};

const buildCalendarCells = ({
  referenceDate,
  dayAvailabilityMap,
  totalUnits,
  todayKey,
  statusFilters,
  mode = 'month',
}) => {
  if (!referenceDate) return [];
  const startDate =
    mode === 'week' ? getStartOfWeek(referenceDate) : getStartOfMonthGrid(referenceDate);
  const totalCells = mode === 'week' ? 7 : 42;
  const monthReference = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const allowMaintenance =
    statusFilters.maintenance || statusFilters.hold || statusFilters.pending;

  const cells = [];
  for (let index = 0; index < totalCells; index += 1) {
    const cellDate = new Date(startDate);
    cellDate.setDate(startDate.getDate() + index);
    const key = formatDateKey(cellDate);
    const info = dayAvailabilityMap.get(key);
    const counts = info?.counts || createStatusCounters();
    const busyCount = counts.reserved || 0;
    const maintenanceCount =
      (counts.maintenance || 0) + (counts.hold || 0) + (counts.pending || 0);
    const availableCount =
      totalUnits > 0 ? Math.max(totalUnits - (busyCount + maintenanceCount), 0) : 0;

    const pills = [];
    if (availableCount > 0 && statusFilters.available) {
      pills.push({
        key: 'available',
        label: `🟢 ${availableCount} disponibles`,
        tone: 'available',
      });
    }
    if (busyCount > 0 && statusFilters.reserved) {
      pills.push({
        key: 'busy',
        label: `🔴 ${busyCount} ocupadas`,
        tone: 'busy',
      });
    }
    if (maintenanceCount > 0 && allowMaintenance) {
      pills.push({
        key: 'maintenance',
        label: `🟡 ${maintenanceCount} mantenimiento`,
        tone: 'maintenance',
      });
    }

    cells.push({
      key,
      label: cellDate.getDate(),
      isCurrentMonth: cellDate.getMonth() === monthReference.getMonth(),
      isToday: key === todayKey,
      pills,
    });
  }
  return cells;
};

const isSameDay = (first, second) => {
  if (!(first instanceof Date) || !(second instanceof Date)) return false;
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
};

export default function CalendarSection({ user, empresaValues, empresaId }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    kind: '',
    payload: null,
  });
  const [updatingRangeStatus, setUpdatingRangeStatus] = useState(false);
  const todayKey = useMemo(() => formatDateKey(new Date()), []);
  const [statusFilters, setStatusFilters] = useState(() => buildDefaultStatusFilters());
  const [dayNotes, setDayNotes] = useState(() => ({}));
  const [dayInspector, setDayInspector] = useState({
    visible: false,
    dateKey: '',
    note: '',
  });
  const [dayInspectorStatus, setDayInspectorStatus] = useState('');
  const [manualEvents, setManualEvents] = useState([]);
  const [manualEditor, setManualEditor] = useState({
    visible: false,
    mode: 'create',
    eventId: '',
    title: '',
    startDate: '',
    endDate: '',
    status: DEFAULT_EVENT_STATUS,
    notes: '',
    priority: DEFAULT_PRIORITY,
  });
  const [manualEditorStatus, setManualEditorStatus] = useState('');
  const [dayTasks, setDayTasks] = useState({});
  const [dayTaskInput, setDayTaskInput] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState('');
  const [calendarView, setCalendarView] = useState('month');
  const [visibleDate, setVisibleDate] = useState(() => new Date());

  const resolvedCompanyId = useMemo(() => {
    if (empresaId) return empresaId;
    if (empresaValues?.id) return empresaValues.id;
    if (empresaValues?.companyId) return empresaValues.companyId;
    if (empresaValues?.company?.id) return empresaValues.company.id;
    if (user?.companyId) return user.companyId;
    if (user?.company?.id) return user.company.id;
    return '';
  }, [empresaId, empresaValues, user]);

  const manualEventsStorageKey = useMemo(
    () =>
      resolvedCompanyId ? `${MANUAL_EVENTS_STORAGE_PREFIX}${resolvedCompanyId}` : '',
    [resolvedCompanyId]
  );
  const dayTasksStorageKey = useMemo(
    () => (resolvedCompanyId ? `${DAY_TASKS_STORAGE_PREFIX}${resolvedCompanyId}` : ''),
    [resolvedCompanyId]
  );

  const fetchUnitsData = useCallback(async () => {
    if (!resolvedCompanyId) return [];
    const response = await listUnits({ companyId: resolvedCompanyId });
    return Array.isArray(response) ? response : [];
  }, [resolvedCompanyId]);

  useEffect(() => {
    let cancelled = false;

    if (!resolvedCompanyId) {
      setUnits([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetchUnitsData()
      .then((result) => {
        if (cancelled) return;
        setUnits(result);
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setUnits([]);
        setError(err?.message || 'No se pudo cargar la disponibilidad de las unidades.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedCompanyId, fetchUnitsData]);

  const handleReload = useCallback(async () => {
    if (!resolvedCompanyId) return;
    setLoading(true);
    try {
      const result = await fetchUnitsData();
      setUnits(result);
      setError('');
    } catch (err) {
      setUnits([]);
      setError(err?.message || 'No se pudo cargar la disponibilidad de las unidades.');
    } finally {
      setLoading(false);
    }
  }, [resolvedCompanyId, fetchUnitsData]);

  const handleResetStatusFilters = useCallback(() => {
    setStatusFilters(buildDefaultStatusFilters());
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    if (!resolvedCompanyId) {
      setDayNotes({});
      return;
    }
    try {
      const stored = window.localStorage.getItem(
        `${DAY_NOTES_STORAGE_PREFIX}${resolvedCompanyId}`
      );
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setDayNotes(parsed);
          return;
        }
      }
      setDayNotes({});
    } catch (storageError) {
      console.warn('No se pudo cargar notas del calendario.', storageError);
      setDayNotes({});
    }
  }, [resolvedCompanyId]);

  useEffect(() => {
    if (!isBrowser) return;
    if (!resolvedCompanyId) return;
    try {
      window.localStorage.setItem(
        `${DAY_NOTES_STORAGE_PREFIX}${resolvedCompanyId}`,
        JSON.stringify(dayNotes)
      );
    } catch (storageError) {
      console.warn('No se pudo guardar notas del calendario.', storageError);
    }
  }, [dayNotes, resolvedCompanyId]);

  useEffect(() => {
    if (!isBrowser) return;
    if (!manualEventsStorageKey) {
      setManualEvents([]);
      return;
    }
    try {
      const stored = window.localStorage.getItem(manualEventsStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setManualEvents(parsed);
          return;
        }
      }
      setManualEvents([]);
    } catch (storageError) {
      console.warn('No se pudo cargar registros operativos manuales.', storageError);
      setManualEvents([]);
    }
  }, [manualEventsStorageKey]);

  useEffect(() => {
    if (!isBrowser) return;
    if (!manualEventsStorageKey) return;
    try {
      window.localStorage.setItem(
        manualEventsStorageKey,
        JSON.stringify(manualEvents)
      );
    } catch (storageError) {
      console.warn('No se pudo guardar registros operativos manuales.', storageError);
    }
  }, [manualEvents, manualEventsStorageKey]);

  useEffect(() => {
    if (!isBrowser) return;
    if (!dayTasksStorageKey) {
      setDayTasks({});
      return;
    }
    try {
      const stored = window.localStorage.getItem(dayTasksStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setDayTasks(parsed);
          return;
        }
      }
      setDayTasks({});
    } catch (storageError) {
      console.warn('No se pudo cargar la checklist del calendario.', storageError);
      setDayTasks({});
    }
  }, [dayTasksStorageKey]);

  useEffect(() => {
    if (!isBrowser) return;
    if (!dayTasksStorageKey) return;
    try {
      window.localStorage.setItem(dayTasksStorageKey, JSON.stringify(dayTasks));
    } catch (storageError) {
      console.warn('No se pudo guardar la checklist del calendario.', storageError);
    }
  }, [dayTasks, dayTasksStorageKey]);

  useEffect(() => {
    if (!isFullscreen) {
      document.body.style.removeProperty('overflow');
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [isFullscreen]);

  const handleDayInspectorOpen = useCallback(
    (dateKey) => {
      if (!dateKey) return;
       setSelectedDayKey(dateKey);
      setDayInspector({
        visible: true,
        dateKey,
        note: dayNotes[dateKey] || '',
      });
      setDayInspectorStatus('');
    },
    [dayNotes]
  );

  const handleDayInspectorClose = useCallback(() => {
    setDayInspector({
      visible: false,
      dateKey: '',
      note: '',
    });
    setDayInspectorStatus('');
    setSelectedDayKey('');
  }, []);

  const handleDayNoteChange = useCallback((event) => {
    const { value } = event.target;
    setDayInspector((prev) => ({
      ...prev,
      note: value,
    }));
    setDayInspectorStatus('');
  }, []);

  const handleDayNoteSave = useCallback(() => {
    if (!dayInspector.dateKey) return;
    const trimmedNote = (dayInspector.note || '').trim();
    if (!trimmedNote) {
      setDayNotes((prev) => {
        const next = { ...prev };
        delete next[dayInspector.dateKey];
        return next;
      });
      setDayInspectorStatus('Comentario vacio eliminado.');
      return;
    }
    setDayNotes((prev) => ({
      ...prev,
      [dayInspector.dateKey]: trimmedNote,
    }));
    setDayInspector((prev) => ({
      ...prev,
      note: trimmedNote,
    }));
    setDayInspectorStatus('Comentario guardado.');
  }, [dayInspector.dateKey, dayInspector.note]);

  const handleDayNoteClear = useCallback(() => {
    if (!dayInspector.dateKey) return;
    setDayNotes((prev) => {
      const next = { ...prev };
      delete next[dayInspector.dateKey];
      return next;
    });
    setDayInspector((prev) => ({
      ...prev,
      note: '',
    }));
    setDayInspectorStatus('Comentario eliminado.');
  }, [dayInspector.dateKey]);

  const inspectorTasks = useMemo(() => {
    if (!dayInspector.dateKey) return [];
    const record = dayTasks[dayInspector.dateKey];
    return Array.isArray(record) ? record : [];
  }, [dayInspector.dateKey, dayTasks]);

  const updateTasksForDay = useCallback((dateKey, updater) => {
    if (!dateKey) return;
    setDayTasks((prev) => {
      const current = Array.isArray(prev[dateKey]) ? prev[dateKey] : [];
      const next = updater(current);
      return {
        ...prev,
        [dateKey]: next,
      };
    });
  }, []);

  const handleTaskInputChange = useCallback((event) => {
    setDayTaskInput(event.target.value);
  }, []);

  const handleAddTask = useCallback(() => {
    if (!dayInspector.dateKey) return;
    const label = dayTaskInput.trim();
    if (!label) return;
    const newTask = {
      id: createTaskId(),
      label,
      done: false,
      createdAt: new Date().toISOString(),
    };
    updateTasksForDay(dayInspector.dateKey, (current) => [...current, newTask]);
    setDayTaskInput('');
  }, [dayInspector.dateKey, dayTaskInput, updateTasksForDay]);

  const handleTaskInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddTask();
      }
    },
    [handleAddTask]
  );

  const handleToggleTask = useCallback(
    (taskId) => {
      if (!dayInspector.dateKey || !taskId) return;
      updateTasksForDay(dayInspector.dateKey, (current) =>
        current.map((task) =>
          task.id === taskId
            ? {
                ...task,
                done: !task.done,
                updatedAt: new Date().toISOString(),
              }
            : task
        )
      );
    },
    [dayInspector.dateKey, updateTasksForDay]
  );

  const handleRemoveTask = useCallback(
    (taskId) => {
      if (!dayInspector.dateKey || !taskId) return;
      updateTasksForDay(dayInspector.dateKey, (current) => current.filter((task) => task.id !== taskId));
    },
    [dayInspector.dateKey, updateTasksForDay]
  );

  const handleClearCompletedTasks = useCallback(() => {
    if (!dayInspector.dateKey) return;
    updateTasksForDay(dayInspector.dateKey, (current) => current.filter((task) => !task.done));
  }, [dayInspector.dateKey, updateTasksForDay]);

  const handleApplyTaskPreset = useCallback(
    (label) => {
      if (!dayInspector.dateKey) return;
      const presetLabel = label.trim();
      if (!presetLabel) return;
      updateTasksForDay(dayInspector.dateKey, (current) => {
        if (current.some((task) => task.label.toLowerCase() === presetLabel.toLowerCase())) {
          return current;
        }
        return [
          ...current,
          {
            id: createTaskId(),
            label: presetLabel,
            done: false,
            createdAt: new Date().toISOString(),
          },
        ];
      });
    },
    [dayInspector.dateKey, updateTasksForDay]
  );

  const closeManualEditor = useCallback(() => {
    setManualEditor((prev) => ({ ...prev, visible: false }));
    setManualEditorStatus('');
  }, []);

  const openManualEditorForDay = useCallback(
    (dateKey, preset = {}) => {
      const safeDate = ensureDateKey(dateKey) || todayKey;
      const presetStart = ensureDateKey(preset.startDate) || safeDate;
      const presetEnd = ensureDateKey(preset.endDate) || presetStart;
      setManualEditor({
        visible: true,
        mode: 'create',
        eventId: '',
        title: preset.title || '',
        startDate: presetStart,
        endDate: presetEnd,
        status: normalizeStatus(preset.status) || DEFAULT_EVENT_STATUS,
        notes: preset.notes || '',
        priority: preset.priority || DEFAULT_PRIORITY,
      });
      setManualEditorStatus('');
    },
    [todayKey]
  );

  const openManualEditorForEvent = useCallback(
    (eventId) => {
      if (!eventId) return;
      const target = manualEvents.find((entry) => entry.id === eventId);
      if (!target) return;
      setManualEditor({
        visible: true,
        mode: 'edit',
        eventId: target.id,
        title: target.title || '',
        startDate: ensureDateKey(target.startDate) || todayKey,
        endDate: ensureDateKey(target.endDate) || ensureDateKey(target.startDate) || todayKey,
        status: normalizeStatus(target.status) || DEFAULT_EVENT_STATUS,
        notes: target.notes || '',
        priority: target.priority || DEFAULT_PRIORITY,
      });
      setManualEditorStatus('');
    },
    [manualEvents, todayKey]
  );

  const handleManualEditorChange = useCallback((field, value) => {
    setManualEditor((prev) => ({
      ...prev,
      [field]: value,
    }));
    setManualEditorStatus('');
  }, []);

  const handleManualEventDelete = useCallback(
    (eventId, { skipConfirm } = {}) => {
      if (!eventId) return;
      if (!skipConfirm && isBrowser) {
        const confirmDelete = window.confirm('¿Eliminar este registro operativo?');
        if (!confirmDelete) {
          return;
        }
      }
      setManualEvents((prev) => prev.filter((entry) => entry.id !== eventId));
      setSelectedEventId((current) => (current === eventId ? '' : current));
      setManualEditor((prev) =>
        prev.eventId === eventId ? { ...prev, visible: false } : prev
      );
      setContextMenu((prev) =>
        prev.payload?.id === eventId
          ? { visible: false, x: 0, y: 0, kind: '', payload: null }
          : prev
      );
    },
    [setContextMenu, setManualEditor, setManualEvents, setSelectedEventId]
  );

  const handleManualEditorSubmit = useCallback(
    (event) => {
      event?.preventDefault();
      const title = (manualEditor.title || '').trim();
      if (!title) {
        setManualEditorStatus('Agrega un nombre para la operación.');
        return;
      }
      const startKey = ensureDateKey(manualEditor.startDate);
      if (!startKey) {
        setManualEditorStatus('Selecciona una fecha de inicio válida.');
        return;
      }
      const endKeyCandidate = ensureDateKey(manualEditor.endDate) || startKey;
      const { start, end } = clampRangeKeys(startKey, endKeyCandidate);
      const payload = {
        title,
        startDate: start,
        endDate: end || start,
        status: normalizeStatus(manualEditor.status || DEFAULT_EVENT_STATUS),
        notes: (manualEditor.notes || '').trim(),
        priority: manualEditor.priority || DEFAULT_PRIORITY,
      };
      if (manualEditor.mode === 'edit' && manualEditor.eventId) {
        setManualEvents((prev) =>
          prev.map((entry) =>
            entry.id === manualEditor.eventId
              ? { ...entry, ...payload, updatedAt: new Date().toISOString() }
              : entry
          )
        );
      } else {
        const nowIso = new Date().toISOString();
        const newEvent = {
          id: createManualEventId(),
          ...payload,
          createdAt: nowIso,
          updatedAt: nowIso,
          source: 'manual',
        };
        setManualEvents((prev) => [...prev, newEvent]);
        setSelectedEventId(newEvent.id);
      }
      closeManualEditor();
    },
    [closeManualEditor, manualEditor, setManualEvents, setSelectedEventId]
  );

  useEffect(() => {
    if (!resolvedCompanyId) {
      return undefined;
    }

    let canceled = false;
    let refreshTimer = null;

    const refreshUnits = async () => {
      try {
        const result = await fetchUnitsData();
        if (!canceled) {
          setUnits(result);
        }
      } catch (err) {
        if (!canceled) {
          console.warn('No se pudieron sincronizar las unidades desde el servidor.', err);
        }
      }
    };

    if (typeof window !== 'undefined') {
      refreshTimer = window.setInterval(refreshUnits, 60000);
    }

    return () => {
      canceled = true;
      if (refreshTimer) {
        window.clearInterval(refreshTimer);
      }
    };
  }, [fetchUnitsData, resolvedCompanyId]);

  const unitEvents = useMemo(() => {
    if (!Array.isArray(units) || units.length === 0) {
      return [];
    }

    const result = [];

    units.forEach((unit) => {
      const ranges = Array.isArray(unit.availabilityRanges)
        ? unit.availabilityRanges
        : [];
      const displayName = getUnitDisplayName(unit);

      ranges.forEach((range, index) => {
        if (!range) return;
        const startCandidate =
          ensureDateKey(range.start) ||
          ensureDateKey(range.from) ||
          ensureDateKey(range.begin);
        const endCandidate =
          ensureDateKey(range.end) ||
          ensureDateKey(range.to) ||
          ensureDateKey(range.hasta) ||
          ensureDateKey(range.finish);

        if (!startCandidate) return;

        const endKeyRaw = endCandidate || startCandidate;
        const startKey =
          startCandidate <= endKeyRaw ? startCandidate : endKeyRaw;
        const endKey =
          startCandidate <= endKeyRaw ? endKeyRaw : startCandidate;

        const endExclusive = addDaysIso(endKey, 1);
        if (!endExclusive) return;

        let rawStatus =
          range.status ||
          range.unitStatus ||
          range.state ||
          range.type;
        if (!rawStatus && Array.isArray(range.approvals)) {
          const acceptedApproval = range.approvals.find(
            approval => approval && approval.status === 'accepted'
          );
          if (acceptedApproval) {
            rawStatus = 'reserved';
          }
        }
        if (!rawStatus && Array.isArray(range.reservations)) {
          const hasAcceptedReservation = range.reservations.some(
            reservation =>
              reservation &&
              (reservation.status === 'accepted' || reservation.status === 'confirmed')
          );
          if (hasAcceptedReservation) {
            rawStatus = 'reserved';
          }
        }
        if (!rawStatus && unit.status && unit.status !== 'available') {
          rawStatus = unit.status;
        }
        if (!rawStatus && (range.source === 'request' || range.source === 'hold')) {
          rawStatus = 'hold';
        }
        const status = normalizeStatus(rawStatus);
        const durationDays = countInclusiveDays(startKey, endKey);
        const isActive = startKey <= todayKey && endKey >= todayKey;
        const isConfirmed = status === 'reserved';

        result.push({
          id: `unit-${unit.id}-${index}-${startKey}-${endKey}`,
          title: displayName,
          start: startKey,
          end: endExclusive,
          allDay: true,
          extendedProps: {
            status,
            unitId: unit.id,
            economicNumber: unit.economicNumber,
            model: unit.model,
            tractorType: unit.tractorType,
            capacity: unit.capacity,
            startDate: startKey,
            endDate: endKey,
            durationDays,
            displayName,
            isActive,
            isConfirmed,
            notes: typeof range.note === 'string' ? range.note : '',
            source: 'unit',
            rangeIndex: index,
          },
        });
      });
    });

    result.sort((a, b) => {
      const startCompare = a.extendedProps.startDate.localeCompare(
        b.extendedProps.startDate
      );
      if (startCompare !== 0) return startCompare;
      const endCompare = a.extendedProps.endDate.localeCompare(
        b.extendedProps.endDate
      );
      if (endCompare !== 0) return endCompare;
      return (a.title || '').localeCompare(b.title || '');
    });

    return result;
  }, [units, todayKey]);

  const manualEventsForCalendar = useMemo(() => {
    if (!manualEvents.length) return [];
    return manualEvents
      .map((entry) => {
        const startKey = ensureDateKey(entry.startDate);
        if (!startKey) return null;
        const endKeyCandidate = ensureDateKey(entry.endDate) || startKey;
        const { start, end } = clampRangeKeys(startKey, endKeyCandidate);
        const finalEnd = end || start;
        const endExclusive = addDaysIso(finalEnd, 1);
        if (!endExclusive) return null;
        const status = normalizeStatus(entry.status || DEFAULT_EVENT_STATUS);
        const durationDays = countInclusiveDays(start, finalEnd);
        const isActive = start <= todayKey && finalEnd >= todayKey;
        const title = entry.title || 'Operación manual';
        const priority = entry.priority || DEFAULT_PRIORITY;
        return {
          id: entry.id,
          title,
          start,
          end: endExclusive,
          allDay: true,
          extendedProps: {
            ...entry,
            status,
            startDate: start,
            endDate: finalEnd,
            durationDays,
            displayName: title,
            isActive,
            isConfirmed: status === 'reserved',
            source: 'manual',
            priority,
          },
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        a.extendedProps.startDate.localeCompare(b.extendedProps.startDate)
      );
  }, [manualEvents, todayKey]);

  const events = useMemo(
    () => [...unitEvents, ...manualEventsForCalendar],
    [unitEvents, manualEventsForCalendar]
  );

  const createManualDraftFromEvent = useCallback(
    (eventData) => {
      if (!eventData?.extendedProps) return;
      openManualEditorForDay(eventData.extendedProps.startDate, {
        title: eventData.extendedProps.displayName || eventData.title,
        startDate: eventData.extendedProps.startDate,
        endDate: eventData.extendedProps.endDate,
        status: eventData.extendedProps.status,
        notes: eventData.extendedProps.notes || '',
      });
    },
    [openManualEditorForDay]
  );

  const filteredEvents = useMemo(() => {
    if (!events.length) return [];
    return events.filter((event) => {
      const status = normalizeStatus(event.extendedProps?.status);
      if (typeof statusFilters[status] === 'boolean') {
        return statusFilters[status];
      }
      return true;
    });
  }, [events, statusFilters]);

  const unitsById = useMemo(() => {
    const map = new Map();
    units.forEach((unit) => {
      map.set(unit.id, unit);
    });
    return map;
  }, [units]);

  useEffect(() => {
    if (!selectedEventId) return;
    if (!filteredEvents.some((event) => event.id === selectedEventId)) {
      setSelectedEventId('');
    }
  }, [filteredEvents, selectedEventId]);

  const selectedEvent = useMemo(
    () => filteredEvents.find((event) => event.id === selectedEventId) || null,
    [filteredEvents, selectedEventId]
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    []
  );

  const calendarTitleFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        month: 'long',
        year: 'numeric',
      }),
    []
  );

  const formatRange = useCallback(
    (startKey, endKey) => {
      const startDate = parseDateKey(startKey);
      const endDate = parseDateKey(endKey);
      if (!startDate) {
        return 'Sin periodo definido';
      }
      if (!endDate || startDate.getTime() === endDate.getTime()) {
        return dateFormatter.format(startDate);
      }
      return `${dateFormatter.format(startDate)} - ${dateFormatter.format(endDate)}`;
    },
    [dateFormatter]
  );

  const totalUnits = units.length;

  const dayAvailabilitySummary = useMemo(() => {
    const map = new Map();

    filteredEvents.forEach((event) => {
      if (!event) return;
      const startKey =
        ensureDateKey(event.extendedProps?.startDate) || ensureDateKey(event.startStr);
      const endKey =
        ensureDateKey(event.extendedProps?.endDate) ||
        ensureDateKey(event.endStr) ||
        startKey;
      if (!startKey) return;
      const status = normalizeStatus(event.extendedProps?.status);
      const keys = listDateKeysInclusive(startKey, endKey);
      keys.forEach((key) => {
        if (!key) return;
        const entry =
          map.get(key) ||
          {
            statuses: new Set(),
            category: '',
            counts: createStatusCounters(),
            totalEvents: 0,
            manualCount: 0,
          };
        entry.statuses.add(status);
        entry.totalEvents += 1;
        entry.counts[status] = (entry.counts[status] || 0) + 1;
        if (event.extendedProps?.source === 'manual') {
          entry.manualCount = (entry.manualCount || 0) + 1;
        }
        map.set(key, entry);
      });
    });

    map.forEach((entry) => {
      const statusList = Array.from(entry.statuses || []);
      const hasOccupied = statusList.some((status) => OCCUPIED_STATUSES.has(status));
      const hasBlocked = statusList.some((status) => BLOCKED_STATUSES.has(status));
      if (hasOccupied) {
        entry.category = 'occupied';
      } else if (hasBlocked) {
        entry.category = 'blocked';
      } else {
        entry.category = 'free';
      }
    });

    return {
      map,
    };
  }, [filteredEvents]);

  const dayAvailabilityMap = useMemo(
    () => dayAvailabilitySummary.map || new Map(),
    [dayAvailabilitySummary]
  );
  const visibleEventCount = filteredEvents.length;
  const calendarTitle = useMemo(
    () => calendarTitleFormatter.format(visibleDate),
    [calendarTitleFormatter, visibleDate]
  );
  const calendarDays = useMemo(
    () =>
      buildCalendarCells({
        referenceDate: visibleDate,
        dayAvailabilityMap,
        totalUnits,
        todayKey,
        statusFilters,
        mode: calendarView,
      }),
    [visibleDate, dayAvailabilityMap, totalUnits, todayKey, statusFilters, calendarView]
  );
  const calendarFilters = useMemo(
    () =>
      CALENDAR_FILTER_GROUPS.map((group) => ({
        ...group,
        isActive: group.statuses.every((status) => statusFilters[status]),
      })),
    [statusFilters]
  );
  const showCalendarFilterReset = calendarFilters.some((filter) => !filter.isActive);
  const inspectorEvents = useMemo(() => {
    if (!dayInspector.dateKey) return [];
    return filteredEvents.filter((event) => {
      const startKey =
        ensureDateKey(event.extendedProps?.startDate) || ensureDateKey(event.startStr);
      const endKey =
        ensureDateKey(event.extendedProps?.endDate) ||
        ensureDateKey(event.endStr) ||
        startKey;
      if (!startKey) return false;
      return startKey <= dayInspector.dateKey && (endKey || startKey) >= dayInspector.dateKey;
    });
  }, [dayInspector.dateKey, filteredEvents]);

  const inspectorManualCount = useMemo(
    () => inspectorEvents.filter((event) => event.extendedProps?.source === 'manual').length,
    [inspectorEvents]
  );


  const inspectorAgenda = useMemo(() => {
    if (!inspectorEvents.length) return [];
    return [...inspectorEvents].sort((a, b) => {
      const aStart = ensureDateKey(a.extendedProps?.startDate) || '';
      const bStart = ensureDateKey(b.extendedProps?.startDate) || '';
      const compareStart = aStart.localeCompare(bStart);
      if (compareStart !== 0) return compareStart;
      return (a.extendedProps?.displayName || '').localeCompare(b.extendedProps?.displayName || '');
    });
  }, [inspectorEvents]);

  const inspectorDateLabel = useMemo(() => {
    if (!dayInspector.dateKey) {
      return '';
    }
    const parsed = parseDateKey(dayInspector.dateKey);
    if (!parsed) return '';
    return dateFormatter.format(parsed);
  }, [dayInspector.dateKey, dateFormatter]);

  const inspectorCategory = useMemo(() => {
    if (!dayInspector.dateKey) return '';
    const info = dayAvailabilityMap.get(dayInspector.dateKey);
    return info?.category || (dayInspector.dateKey < todayKey ? 'past' : 'free');
  }, [dayAvailabilityMap, dayInspector.dateKey, todayKey]);
  const inspectorCategoryLabel =
    DAY_CATEGORY_LABELS[inspectorCategory] || DAY_CATEGORY_LABELS.free;
  const inspectorNoteValue = dayInspector.note || '';
  const inspectorHasSavedNote = Boolean(
    dayInspector.dateKey &&
      typeof dayNotes[dayInspector.dateKey] === 'string' &&
      dayNotes[dayInspector.dateKey].trim()
  );

  const calendarRef = useRef(null);

  const handleDatesSet = useCallback((payload) => {
    if (!payload) return;
    const anchorDate =
      (payload.view && payload.view.currentStart) || payload.start || new Date();
    const displayDate = anchorDate instanceof Date ? anchorDate : new Date(anchorDate);
    setVisibleDate((prev) => {
      if (prev && isSameDay(prev, displayDate)) {
        return prev;
      }
      return displayDate;
    });
    if (payload.view?.type) {
      const nextView = payload.view.type === 'timeGridWeek' ? 'week' : 'month';
      setCalendarView((prev) => (prev === nextView ? prev : nextView));
    }
  }, []);

  const handleCalendarViewChange = useCallback(
    (nextView) => {
      if (!nextView || nextView === calendarView) return;
      setCalendarView(nextView);
      const api = calendarRef.current?.getApi?.();
      if (!api) {
        return;
      }
      if (nextView === 'week') {
        api.changeView('timeGridWeek');
      } else {
        api.changeView('dayGridMonth');
      }
    },
    [calendarView]
  );

  const handleCalendarPrev = useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    api?.prev();
  }, []);

  const handleCalendarNext = useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    api?.next();
  }, []);

  const handleCalendarToday = useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    api?.today();
  }, []);

  const handleCalendarFilterToggle = useCallback((filterKey) => {
    const target = CALENDAR_FILTER_GROUPS.find((filter) => filter.key === filterKey);
    if (!target) return;
    setStatusFilters((prev) => {
      const shouldEnable = !target.statuses.every((status) => prev[status]);
      const next = {
        ...prev,
      };
      target.statuses.forEach((status) => {
        next[status] = shouldEnable;
      });
      const hasActive = EVENT_STATUS_ORDER.some((status) => next[status]);
      return hasActive ? next : prev;
    });
  }, []);

  const handleCalendarDaySelect = useCallback(
    (dayKey) => {
      if (!dayKey) return;
      setSelectedDayKey(dayKey);
      handleDayInspectorOpen(dayKey);
      const targetDate = parseDateKey(dayKey);
      if (targetDate && calendarRef.current?.getApi?.()) {
        calendarRef.current.getApi().gotoDate(targetDate);
      }
    },
    [handleDayInspectorOpen]
  );

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const handleEventClick = useCallback((clickInfo) => {
    setSelectedEventId((current) =>
      current === clickInfo.event.id ? '' : clickInfo.event.id
    );
  }, []);

  const handleDateClick = useCallback(
    (arg) => {
      const key = formatDateKey(arg.date);
      handleDayInspectorOpen(key);
    },
    [handleDayInspectorOpen]
  );

  const openContextMenu = useCallback((clientX, clientY, config = {}) => {
    const menuWidth = config.kind === 'day' ? 240 : 260;
    const menuHeight = config.kind === 'day' ? 190 : 260;
    const x = isBrowser ? Math.min(clientX, window.innerWidth - menuWidth - 8) : clientX;
    const y = isBrowser ? Math.min(clientY, window.innerHeight - menuHeight - 8) : clientY;
    setContextMenu({
      visible: true,
      x,
      y,
      kind: config.kind || 'unit-event',
      payload: config.payload || null,
    });
  }, []);

  const handleCalendarDayContextMenu = useCallback(
    (event, dayKey) => {
      if (!dayKey) return;
      openContextMenu(event.clientX, event.clientY, {
        kind: 'day',
        payload: { dateKey: dayKey },
      });
    },
    [openContextMenu]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      kind: '',
      payload: null,
    });
  }, []);

  const handleContextMenuCreateManual = useCallback(() => {
    if (contextMenu.kind !== 'day') return;
    openManualEditorForDay(contextMenu.payload?.dateKey);
    closeContextMenu();
  }, [closeContextMenu, contextMenu, openManualEditorForDay]);

  const handleContextMenuOpenInspector = useCallback(() => {
    const dateKey =
      contextMenu.kind === 'day'
        ? contextMenu.payload?.dateKey
        : contextMenu.payload?.targetDate || contextMenu.payload?.startDate;
    if (dateKey) {
      handleDayInspectorOpen(dateKey);
    }
    closeContextMenu();
  }, [contextMenu, closeContextMenu, handleDayInspectorOpen]);

  const handleContextMenuEditManual = useCallback(() => {
    if (contextMenu.kind !== 'manual-event') return;
    openManualEditorForEvent(contextMenu.payload?.id);
    closeContextMenu();
  }, [closeContextMenu, contextMenu, openManualEditorForEvent]);

  const handleContextMenuDeleteManual = useCallback(() => {
    if (contextMenu.kind !== 'manual-event') return;
    handleManualEventDelete(contextMenu.payload?.id);
    closeContextMenu();
  }, [closeContextMenu, contextMenu, handleManualEventDelete]);

  const handleContextMenuPromoteToManual = useCallback(() => {
    if (contextMenu.kind !== 'unit-event') return;
    const match = events.find((evt) => evt.id === contextMenu.payload?.id);
    if (!match) return;
    createManualDraftFromEvent(match);
    closeContextMenu();
  }, [closeContextMenu, contextMenu, createManualDraftFromEvent, events]);

  useEffect(() => {
    if (!contextMenu.visible) return undefined;
    const handleDismiss = () => {
      closeContextMenu();
    };
    window.addEventListener('click', handleDismiss);
    window.addEventListener('contextmenu', handleDismiss);
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      window.removeEventListener('click', handleDismiss);
      window.removeEventListener('contextmenu', handleDismiss);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [contextMenu.visible, closeContextMenu]);

  const updateRangeStatusForEvent = useCallback(
    async (targetEvent, nextStatus) => {
      if (!targetEvent) {
        throw new Error('No se encontro el evento seleccionado.');
      }
      const targetUnit = units.find((unit) => unit.id === targetEvent.unitId);
      if (!targetUnit) {
        throw new Error('No se encontro la unidad seleccionada.');
      }
      const ranges = Array.isArray(targetUnit.availabilityRanges)
        ? targetUnit.availabilityRanges.map((range) => ({ ...range }))
        : [];
      let rangeIndex = Number.isInteger(targetEvent.rangeIndex)
        ? targetEvent.rangeIndex
        : -1;
      if (rangeIndex < 0 || !ranges[rangeIndex]) {
        const eventStart = ensureDateKey(targetEvent.startDate);
        const eventEnd = ensureDateKey(targetEvent.endDate);
        rangeIndex = ranges.findIndex((range) => {
          const candidateStart =
            ensureDateKey(range.start) ||
            ensureDateKey(range.from) ||
            ensureDateKey(range.begin);
          const candidateEnd =
            ensureDateKey(range.end) ||
            ensureDateKey(range.to) ||
            ensureDateKey(range.hasta) ||
            ensureDateKey(range.finish) ||
            candidateStart;
          return candidateStart === eventStart && candidateEnd === eventEnd;
        });
        if (rangeIndex === -1) {
          throw new Error('No se encontro el periodo seleccionado dentro de la unidad.');
        }
      }
      const rangeToUpdate = ranges[rangeIndex];
      const rangeStart = getRangeStartKey(rangeToUpdate);
      const rangeEnd = getRangeEndKey(rangeToUpdate);
      const canonicalStatus = normalizeStatus(nextStatus);
      const currentRangeStatus = resolveRangeStatus(rangeToUpdate, targetUnit.status);
      const targetDayKey = ensureDateKey(
        targetEvent.targetDate || targetEvent.startDate || rangeStart
      );

      if (!rangeStart || !rangeEnd || !targetDayKey) {
        throw new Error('No se encontro el periodo seleccionado dentro de la unidad.');
      }

      if (
        canonicalStatus === currentRangeStatus &&
        rangeStart === rangeEnd &&
        rangeStart === targetDayKey
      ) {
        return;
      }

      const segments = [];

      if (compareDateKeys(rangeStart, targetDayKey) < 0) {
        const previousEnd = addDaysIso(targetDayKey, -1);
        if (previousEnd) {
          segments.push(cloneRangeWithBounds(rangeToUpdate, rangeStart, previousEnd));
        }
      }

      if (canonicalStatus !== 'available') {
        const middleSegment = cloneRangeWithBounds(rangeToUpdate, targetDayKey, targetDayKey);
        segments.push({
          ...middleSegment,
          status: canonicalStatus,
          state: canonicalStatus,
          type: canonicalStatus,
          unitStatus: canonicalStatus,
        });
      }

      if (compareDateKeys(rangeEnd, targetDayKey) > 0) {
        const nextStart = addDaysIso(targetDayKey, 1);
        if (nextStart) {
          segments.push(cloneRangeWithBounds(rangeToUpdate, nextStart, rangeEnd));
        }
      }

      if (segments.length === 0) {
        ranges.splice(rangeIndex, 1);
      } else {
        ranges.splice(rangeIndex, 1, ...segments);
      }

      await updateUnit(targetEvent.unitId, { availabilityRanges: ranges });
      setUnits((prev) =>
        prev.map((unit) =>
          unit.id === targetUnit.id ? { ...unit, availabilityRanges: ranges } : unit
        )
      );
    },
    [units]
  );

  const handleStatusUpdate = useCallback(
    async (nextStatus, domEvent) => {
      domEvent?.stopPropagation();
      domEvent?.preventDefault();
      const normalized = normalizeStatus(nextStatus);
      if (contextMenu.kind === 'manual-event' && contextMenu.payload?.id) {
        setManualEvents((prev) =>
          prev.map((entry) =>
            entry.id === contextMenu.payload.id ? { ...entry, status: normalized } : entry
          )
        );
        closeContextMenu();
        return;
      }
      if (contextMenu.kind !== 'unit-event' || !contextMenu.payload || updatingRangeStatus) {
        return;
      }
      setUpdatingRangeStatus(true);
      try {
        await updateRangeStatusForEvent(contextMenu.payload, normalized);
        closeContextMenu();
      } catch (statusError) {
        setError(
          statusError?.message ||
            'No se pudo actualizar el estado de la unidad seleccionada.'
        );
      } finally {
        setUpdatingRangeStatus(false);
      }
    },
    [contextMenu, closeContextMenu, setManualEvents, updateRangeStatusForEvent, updatingRangeStatus]
  );

  const contextMenuStatus = useMemo(() => {
    if (!contextMenu.payload?.id || contextMenu.kind === 'day') return '';
    const match = events.find((evt) => evt.id === contextMenu.payload.id);
    if (!match) return '';
    return normalizeStatus(match.extendedProps?.status);
  }, [contextMenu.kind, contextMenu.payload, events]);

  const contextMenuDateLabel = useMemo(() => {
    if (contextMenu.kind !== 'day' || !contextMenu.payload?.dateKey) return '';
    const parsed = parseDateKey(contextMenu.payload.dateKey);
    if (!parsed) return contextMenu.payload.dateKey;
    return dateFormatter.format(parsed);
  }, [contextMenu, dateFormatter]);

  const handleClearSelection = useCallback(() => {
    setSelectedEventId('');
    closeContextMenu();
  }, [closeContextMenu]);

  const dayCellClassNames = useCallback(
    (arg) => {
      const dateKey = formatDateKey(arg.date);
      const classes = ['calendar-day'];
      if (!dateKey) {
        return classes;
      }
      if (dateKey === todayKey) {
        classes.push('calendar-day--today');
      }
      if (selectedDayKey && dateKey === selectedDayKey) {
        classes.push('calendar-day--selected');
      }
      const dayInfo = dayAvailabilityMap.get(dateKey);
      if (dayInfo?.manualCount) {
        classes.push('calendar-day--has-manual');
      }
      if (dateKey < todayKey) {
        classes.push('calendar-day--past');
        return classes;
      }
      if (dayInfo?.category === 'occupied') {
        classes.push('calendar-day--occupied');
        return classes;
      }
      if (dayInfo?.category === 'blocked') {
        classes.push('calendar-day--blocked');
        return classes;
      }
      classes.push('calendar-day--free');
      return classes;
    },
    [dayAvailabilityMap, todayKey, selectedDayKey]
  );

  const handleAgendaEventLink = useCallback(
    (eventId, dateKey) => {
      if (!eventId) return;
      setSelectedEventId(eventId);
      if (calendarRef.current && dateKey) {
        const api = calendarRef.current.getApi?.();
        const targetDate = parseDateKey(dateKey);
        if (api && targetDate) {
          api.gotoDate(targetDate);
        }
      }
      handleDayInspectorClose();
    },
    [handleDayInspectorClose]
  );

  const dayCellContent = useCallback(
    (arg) => {
      const dateKey = formatDateKey(arg.date);
      const dayNumber = arg.date.getDate();
      const dayInfo = dateKey ? dayAvailabilityMap.get(dateKey) : null;
      const counts = dayInfo?.counts || {};
      const busyCount = counts.reserved || 0;
      const holdCount = counts.hold || 0;
      const maintenanceCount = counts.maintenance || 0;
      const pendingCount = counts.pending || 0;
      const blockedCount = holdCount + maintenanceCount + pendingCount;
      const manualCount = dayInfo?.manualCount || 0;
      const eventsCount = dayInfo?.totalEvents || 0;
      const hasNotes = Boolean(dateKey && dayNotes[dateKey]);
      const summaryCategory =
        dayInfo?.category || (dateKey && dateKey < todayKey ? 'past' : 'free');
      const computedFree =
        totalUnits > 0 ? Math.max(totalUnits - (busyCount + blockedCount), 0) : 0;
      const capacityFallback = busyCount + blockedCount + computedFree;
      const baseTotal = Math.max(totalUnits, capacityFallback, 1);
      const segments = [
        { value: busyCount, type: 'reserved' },
        { value: holdCount, type: 'hold' },
        { value: maintenanceCount, type: 'maintenance' },
        { value: pendingCount, type: 'pending' },
        { value: computedFree, type: 'available' },
      ].filter((segment) => segment.value > 0);
      const barSegments =
        segments.length > 0
          ? segments
              .map(
                (segment) =>
                  `<span class="calendar-heatmap__bar-segment calendar-heatmap__bar-segment--${segment.type}" style="flex:${segment.value /
                    baseTotal};"></span>`
              )
              .join('')
          : '<span class="calendar-heatmap__bar-segment calendar-heatmap__bar-segment--empty"></span>';
      const tooltipParts = [];
      if (eventsCount) {
        tooltipParts.push(
          `${eventsCount} ${eventsCount === 1 ? 'operacion' : 'operaciones'}`
        );
      }
      if (busyCount) {
        tooltipParts.push(
          `${busyCount} ${busyCount === 1 ? 'unidad ocupada' : 'unidades ocupadas'}`
        );
      }
      if (blockedCount) {
        tooltipParts.push(
          `${blockedCount} ${blockedCount === 1 ? 'unidad bloqueada' : 'unidades bloqueadas'}`
        );
      }
      if (computedFree) {
        tooltipParts.push(
          `${computedFree} ${computedFree === 1 ? 'unidad libre' : 'unidades libres'}`
        );
      }
      if (manualCount) {
        tooltipParts.push(
          `${manualCount} ${manualCount === 1 ? 'registro manual' : 'registros manuales'}`
        );
      }
      const tooltipText =
        tooltipParts.length > 0 ? tooltipParts.join(' · ') : 'Sin operaciones';
      const safeTooltip = tooltipText.replace(/"/g, '&quot;');
      const noteMarker = hasNotes
        ? '<span class="calendar-heatmap__marker calendar-heatmap__marker--note" aria-label="Nota operativa"></span>'
        : '';
      const manualMarker = manualCount
        ? `<span class="calendar-heatmap__marker calendar-heatmap__marker--manual" aria-label="${manualCount} registros manuales" data-count="${manualCount}"></span>`
        : '';
      const markers =
        noteMarker || manualMarker
          ? `<span class="calendar-heatmap__markers">${noteMarker}${manualMarker}</span>`
          : '';
      const html = `
        <div class="calendar-heatmap-cell calendar-heatmap-cell--${summaryCategory}" data-tooltip="${safeTooltip}" title="${safeTooltip}">
          <div class="calendar-heatmap-cell__header">
            <span class="calendar-heatmap__date">${dayNumber}</span>
            ${markers}
          </div>
          <div class="calendar-heatmap-cell__body">
            <div class="calendar-heatmap__bar" role="presentation">
              ${barSegments}
            </div>
          </div>
        </div>
      `;
      return { html };
    },
    [dayAvailabilityMap, totalUnits, todayKey, dayNotes]
  );

  const dayCellDidMount = useCallback(
    (info) => {
      const handler = (domEvent) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        const dateKey = formatDateKey(info.date);
        openContextMenu(domEvent.clientX, domEvent.clientY, {
          kind: 'day',
          payload: { dateKey },
        });
      };
      info.el.addEventListener('contextmenu', handler);
      return () => {
        info.el.removeEventListener('contextmenu', handler);
      };
    },
    [openContextMenu]
  );

  const eventClassNames = useCallback((arg) => {
    const status = normalizeStatus(arg.event.extendedProps?.status);
    const classes = ['calendar-event', `calendar-event--${status}`];
    if (arg.event.extendedProps?.isActive && arg.event.extendedProps?.isConfirmed) {
      classes.push('calendar-event--active');
    }
    if (arg.event.extendedProps?.source === 'manual') {
      classes.push('calendar-event--manual');
      const priority = arg.event.extendedProps?.priority || DEFAULT_PRIORITY;
      classes.push(`calendar-event--priority-${priority}`);
    }
    return classes;
  }, []);

  const eventDidMount = useCallback(
    (info) => {
      const handler = (domEvent) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        const { event } = info;
        const startKey = ensureDateKey(event.extendedProps?.startDate || event.startStr);
        const endKey = ensureDateKey(event.extendedProps?.endDate || event.endStr || startKey);
        const fallbackDate = ensureDateKey(event.startStr);
        const targetDate = resolveDateKeyFromPoint(
          info.view,
          startKey,
          endKey,
          domEvent.clientX,
          domEvent.clientY,
          fallbackDate
        );
        setSelectedEventId((current) => (current === event.id ? current : event.id));
        openContextMenu(domEvent.clientX, domEvent.clientY, {
          kind: event.extendedProps?.source === 'manual' ? 'manual-event' : 'unit-event',
          payload: {
            id: event.id,
            unitId: event.extendedProps?.unitId,
            rangeIndex: event.extendedProps?.rangeIndex ?? -1,
            startDate: event.extendedProps?.startDate,
            endDate: event.extendedProps?.endDate,
            targetDate,
          },
        });
      };
      info.el.addEventListener('contextmenu', handler);
      return () => {
        info.el.removeEventListener('contextmenu', handler);
      };
    },
    [openContextMenu]
  );

  const eventContent = useCallback((arg) => {
    const { extendedProps } = arg.event;
    const displayName = extendedProps?.displayName || arg.event.title || 'Unidad';
    const economicNumber = extendedProps?.economicNumber || '';
    const badgeSource =
      economicNumber ||
      (extendedProps?.tractorType ? String(extendedProps.tractorType) : '') ||
      displayName;
    const badge = badgeSource.slice(0, 8).toUpperCase();
    const maxLength = 22;
    const title =
      displayName.length > maxLength
        ? `${displayName.slice(0, maxLength - 1).trim()}…`
        : displayName;
    const isManual = extendedProps?.source === 'manual';
    const priority = extendedProps?.priority || DEFAULT_PRIORITY;
    const priorityLabel = PRIORITY_LABELS[priority] || 'Manual';

    return (
      <div className="calendar-event__content">
        {badge ? <span className="calendar-event__badge">{badge}</span> : null}
        <div className="calendar-event__info">
          <span className="calendar-event__title">{title}</span>
          {isManual ? (
            <span className={`calendar-event__meta calendar-event__meta--${priority}`}>
              {priorityLabel}
            </span>
          ) : null}
        </div>
      </div>
    );
  }, []);

  const moreLinkContent = useCallback((args) => {
    const count = Number(args.num) || 0;
    return `+${count} ${count === 1 ? 'unidad' : 'unidades'}`;
  }, []);

  const renderSummaryCard = () => {
    if (selectedEvent) {
      const { extendedProps } = selectedEvent;
      const status = normalizeStatus(extendedProps.status);
      const statusLabel = STATUS_LABELS[status] || STATUS_LABELS[DEFAULT_EVENT_STATUS];
      const unit = unitsById.get(extendedProps.unitId);
      if (extendedProps.source === 'manual') {
        const priority = extendedProps.priority || DEFAULT_PRIORITY;
        return (
          <div className="calendar-panel card calendar-panel--manual">
            <div className="calendar-panel__status-row">
              <span className={`calendar-tag calendar-tag--${status}`}>
                {statusLabel}
              </span>
              <span className={`calendar-priority-chip calendar-priority-chip--${priority}`}>
                {PRIORITY_LABELS[priority] || 'Manual'}
              </span>
            </div>
            <h3>{extendedProps.displayName}</h3>
            <dl className="calendar-panel__meta">
              <div>
                <dt>Periodo</dt>
                <dd>{formatRange(extendedProps.startDate, extendedProps.endDate)}</dd>
              </div>
              <div>
                <dt>Duracion</dt>
                <dd>
                  {extendedProps.durationDays || 1}{' '}
                  {extendedProps.durationDays === 1 ? 'dia' : 'dias'}
                </dd>
              </div>
              <div>
                <dt>Notas</dt>
                <dd>{extendedProps.notes || 'Sin notas definidas'}</dd>
              </div>
            </dl>
            <div className="calendar-panel__actions">
              <button type="button" onClick={() => openManualEditorForEvent(selectedEvent.id)}>
                Editar registro
              </button>
              <button type="button" onClick={() => handleManualEventDelete(selectedEvent.id)}>
                Eliminar
              </button>
              <button
                type="button"
                onClick={() => handleDayInspectorOpen(extendedProps.startDate)}
              >
                Abrir dia
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="calendar-panel card">
          <div className="calendar-panel__status-row">
            <span className={`calendar-tag calendar-tag--${status}`}>
              {statusLabel}
            </span>
            {extendedProps.isActive ? (
              <span className="calendar-panel__live-tag">En curso</span>
            ) : null}
          </div>
          <h3>{extendedProps.displayName}</h3>
          <dl className="calendar-panel__meta">
            <div>
              <dt>Periodo</dt>
              <dd>{formatRange(extendedProps.startDate, extendedProps.endDate)}</dd>
            </div>
            <div>
              <dt>Dias comprometidos</dt>
              <dd>
                {extendedProps.durationDays || 1}{' '}
                {extendedProps.durationDays === 1 ? 'dia' : 'dias'}
              </dd>
            </div>
            <div>
              <dt>Tipo de unidad</dt>
              <dd>{unit?.tractorType || 'No registrado'}</dd>
            </div>
            <div>
              <dt>Capacidad</dt>
              <dd>{unit?.capacity || 'Sin datos'}</dd>
            </div>
          </dl>
          {extendedProps.notes ? (
            <p className="calendar-panel__hint">{extendedProps.notes}</p>
          ) : (
            <p className="calendar-panel__hint">
              Ajusta este rango desde la seccion Unidades para liberar o extender la disponibilidad.
            </p>
          )}
          <div className="calendar-panel__actions">
            <button
              type="button"
              onClick={() => handleDayInspectorOpen(extendedProps.startDate)}
            >
              Abrir dia
            </button>
            <button type="button" onClick={() => createManualDraftFromEvent(selectedEvent)}>
              Clonar como registro manual
            </button>
          </div>
          {error ? (
            <div className="calendar-panel__error">
              <span>{error}</span>
              <button type="button" onClick={handleReload}>
                Reintentar
              </button>
            </div>
          ) : null}
          <button type="button" className="calendar-panel__link" onClick={handleClearSelection}>
            Cerrar detalle
          </button>
        </div>
      );
    }

    return null;
  };

  const summaryCard = renderSummaryCard();
  const showLoadingSkeleton = loading && !filteredEvents.length;

  return (
    <section className={`calendar-section${isFullscreen ? ' calendar-section--fullscreen' : ''}`}>
      <div className="calendar-section__body">
        <div
          className={`calendar-board-wrapper${
            isFullscreen ? ' calendar-board-wrapper--fullscreen' : ''
          }`}
        >
          <div className="calendar-board">
            {showLoadingSkeleton ? (
              <div className="calendar-board__skeleton" aria-hidden="true">
                <div className="calendar-board__skeleton-header" />
                <div className="calendar-board__skeleton-grid">
                  {Array.from({ length: 12 }).map((_, index) => (
                    <span key={`skeleton-cell-${index}`} />
                  ))}
                </div>
              </div>
            ) : null}

            <OperationalCalendar
              title={calendarTitle || 'Calendario operativo'}
              view={calendarView}
              viewOptions={CALENDAR_VIEW_OPTIONS}
              onViewChange={handleCalendarViewChange}
              onPrev={handleCalendarPrev}
              onNext={handleCalendarNext}
              onToday={handleCalendarToday}
              weekdays={WEEKDAY_LABELS}
              days={calendarDays}
              selectedDayKey={selectedDayKey}
              onDaySelect={handleCalendarDaySelect}
              onDayContextMenu={handleCalendarDayContextMenu}
              filters={calendarFilters}
              onFilterToggle={handleCalendarFilterToggle}
              onFilterReset={handleResetStatusFilters}
              showReset={showCalendarFilterReset}
              visibleEventCount={visibleEventCount}
              onToggleFullscreen={handleToggleFullscreen}
              isFullscreen={isFullscreen}
            />

            <div className="calendar-board__legacy" aria-hidden="true">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                headerToolbar={false}
                datesSet={handleDatesSet}
                locales={[esLocale]}
                locale="es"
                buttonText={{
                  today: 'Hoy',
                  month: 'Mes',
                  week: 'Semana',
                  day: 'Dia',
                  list: 'Agenda',
                }}
                aspectRatio={1.6}
                height="100%"
                contentHeight="auto"
                expandRows
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
                nowIndicator
                dayMaxEvents={3}
                dayMaxEventRows={3}
                weekends
                events={filteredEvents}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                eventClassNames={eventClassNames}
                dayCellClassNames={dayCellClassNames}
                dayCellContent={dayCellContent}
                dayCellDidMount={dayCellDidMount}
                eventDidMount={eventDidMount}
                eventContent={eventContent}
                moreLinkContent={moreLinkContent}
                moreLinkClick="popover"
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                }}
                selectable={false}
                editable={false}
              />
            </div>
          </div>
          {isFullscreen ? (
            <button
              type="button"
              className="calendar-fullscreen__close"
              onClick={handleToggleFullscreen}
            >
              ×
            </button>
          ) : null}
        </div>

        {summaryCard ? (
          <aside className="calendar-controls">
            <div className="calendar-controls__section">{summaryCard}</div>
          </aside>
        ) : null}
      </div>
      {dayInspector.visible ? (
        <div
          className="calendar-inspector__overlay"
          role="presentation"
          onClick={handleDayInspectorClose}
        >
          <div
            className="calendar-inspector"
            role="dialog"
            aria-modal="true"
            aria-label={`Resumen del ${inspectorDateLabel || dayInspector.dateKey}`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="calendar-inspector__header">
              <div>
                <span className="calendar-inspector__eyebrow">Dia seleccionado</span>
                <h2>{inspectorDateLabel || dayInspector.dateKey}</h2>
                <p className="calendar-inspector__status-label">{inspectorCategoryLabel}</p>
              </div>
              <button
                type="button"
                className="calendar-inspector__close"
                onClick={handleDayInspectorClose}
                aria-label="Cerrar desglose del dia"
              >
                ×
              </button>
            </header>
            <div className="calendar-inspector__body">
              <section className="calendar-inspector__section">
                <div className="calendar-inspector__section-heading">
                  <h3>Agenda del dia</h3>
                  <span>
                    {inspectorAgenda.length
                      ? `${inspectorManualCount} manuales · ${inspectorEvents.length - inspectorManualCount} unidades`
                      : 'Sin registros'}
                  </span>
                </div>
                {inspectorAgenda.length ? (
                  <div className="calendar-agenda">
                    {inspectorAgenda.map((event) => {
                      const status = normalizeStatus(event.extendedProps?.status);
                      const statusLabel =
                        STATUS_LABELS[status] || STATUS_LABELS[DEFAULT_EVENT_STATUS];
                      const isManual = event.extendedProps?.source === 'manual';
                      const priority = event.extendedProps?.priority || DEFAULT_PRIORITY;
                      const manualLabel = isManual ? PRIORITY_LABELS[priority] || 'Manual' : '';
                      const durationLabel = `${event.extendedProps?.durationDays || 1} ${
                        event.extendedProps?.durationDays === 1 ? 'dia' : 'dias'
                      }`;
                      return (
                        <article key={event.id} className="calendar-agenda__item">
                          <div className="calendar-agenda__time">
                            <span>{formatRange(event.extendedProps?.startDate, event.extendedProps?.endDate)}</span>
                            <span className="calendar-agenda__duration">{durationLabel}</span>
                          </div>
                          <div className="calendar-agenda__body">
                            <div className="calendar-agenda__title-row">
                              <button
                                type="button"
                                className="calendar-agenda__title-link"
                                onClick={() =>
                                  handleAgendaEventLink(event.id, event.extendedProps?.startDate)
                                }
                              >
                                {event.extendedProps?.displayName || event.title}
                              </button>
                              <span className={`calendar-badge calendar-badge--${status}`}>
                                {statusLabel}
                              </span>
                            </div>
                            <p className="calendar-agenda__meta">
                              {isManual ? 'Registro manual' : 'Unidad sincronizada'} · {durationLabel}
                            </p>
                            {event.extendedProps?.notes ? (
                              <p className="calendar-agenda__notes">{event.extendedProps.notes}</p>
                            ) : null}
                            {isManual ? (
                              <span
                                className={`calendar-badge calendar-badge--manual calendar-badge--priority-${priority}`}
                              >
                                {manualLabel}
                              </span>
                            ) : null}
                          </div>
                          {isManual ? (
                            <div className="calendar-agenda__actions">
                              <button type="button" onClick={() => openManualEditorForEvent(event.id)}>
                                Editar
                              </button>
                              <button type="button" onClick={() => handleManualEventDelete(event.id)}>
                                Eliminar
                              </button>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <p className="calendar-inspector__empty">
                    No hay operaciones programadas en este dia. Aprovecha para asignar nuevas rutas.
                  </p>
                )}
              </section>
              <section className="calendar-inspector__section">
                <div className="calendar-inspector__section-heading">
                  <h3>Comentarios operativos</h3>
                  {inspectorHasSavedNote ? <span>Guardado localmente</span> : null}
                </div>
                <textarea
                  value={inspectorNoteValue}
                  onChange={handleDayNoteChange}
                  placeholder="Agrega notas rapidas para tu equipo operativo..."
                />
                <div className="calendar-inspector__comment-actions">
                  <button type="button" onClick={handleDayNoteSave}>
                    Guardar nota
                  </button>
                  <button
                    type="button"
                    onClick={handleDayNoteClear}
                    disabled={!inspectorHasSavedNote && !inspectorNoteValue.trim()}
                  >
                    Limpiar
                  </button>
                </div>
                {dayInspectorStatus ? (
                  <span className="calendar-inspector__status" aria-live="polite">
                    {dayInspectorStatus}
                  </span>
                ) : null}
              </section>
              <section className="calendar-inspector__section calendar-inspector__section--checklist">
                <div className="calendar-inspector__section-heading">
                  <h3>Checklist operativo</h3>
                  {inspectorTasks.length ? (
                    <button type="button" onClick={handleClearCompletedTasks}>
                      Limpiar completados
                    </button>
                  ) : null}
                </div>
                <div className="calendar-checklist__form">
                  <input
                    type="text"
                    value={dayTaskInput}
                    onChange={handleTaskInputChange}
                    onKeyDown={handleTaskInputKeyDown}
                    placeholder="Agrega un pendiente para este dia"
                  />
                  <button type="button" onClick={handleAddTask} disabled={!dayTaskInput.trim()}>
                    Agregar
                  </button>
                </div>
                <div className="calendar-checklist__presets">
                  {TASK_PRESETS.map((preset) => (
                    <button type="button" key={preset} onClick={() => handleApplyTaskPreset(preset)}>
                      {preset}
                    </button>
                  ))}
                </div>
                {inspectorTasks.length ? (
                  <ul className="calendar-checklist__list">
                    {inspectorTasks.map((task) => (
                      <li
                        key={task.id}
                        className={task.done ? 'calendar-checklist__item is-complete' : 'calendar-checklist__item'}
                      >
                        <label>
                          <input
                            type="checkbox"
                            checked={task.done}
                            onChange={() => handleToggleTask(task.id)}
                          />
                          <span>{task.label}</span>
                        </label>
                        <button type="button" onClick={() => handleRemoveTask(task.id)} aria-label="Eliminar pendiente">
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="calendar-inspector__empty">Crea un checklist rapido para coordinar tu equipo.</p>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : null}
      {manualEditor.visible ? (
        <div
          className="calendar-planner__overlay"
          role="presentation"
          onClick={closeManualEditor}
        >
          <form
            className="calendar-planner"
            onSubmit={handleManualEditorSubmit}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="calendar-planner__header">
              <div>
                <span className="calendar-planner__eyebrow">
                  {manualEditor.mode === 'edit' ? 'Editar registro' : 'Nueva operación manual'}
                </span>
                <h3>
                  {manualEditor.mode === 'edit'
                    ? 'Actualizar operación manual'
                    : 'Registrar operación manual'}
                </h3>
              </div>
              <button type="button" className="calendar-planner__close" onClick={closeManualEditor}>
                ×
              </button>
            </header>
            <label className="calendar-planner__field">
              <span>Nombre</span>
              <input
                type="text"
                value={manualEditor.title}
                onChange={(event) => handleManualEditorChange('title', event.target.value)}
                placeholder="Rastreo, entrega, mantenimiento, etc."
              />
            </label>
            <div className="calendar-planner__row">
              <label className="calendar-planner__field">
                <span>Inicio</span>
                <input
                  type="date"
                  value={manualEditor.startDate}
                  onChange={(event) =>
                    handleManualEditorChange('startDate', ensureDateKey(event.target.value))
                  }
                />
              </label>
              <label className="calendar-planner__field">
                <span>Fin</span>
                <input
                  type="date"
                  value={manualEditor.endDate}
                  onChange={(event) =>
                    handleManualEditorChange('endDate', ensureDateKey(event.target.value))
                  }
                />
              </label>
            </div>
            <label className="calendar-planner__field">
              <span>Estado operativo</span>
              <select
                value={manualEditor.status}
                onChange={(event) => handleManualEditorChange('status', event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="calendar-planner__priority">
              <span>Prioridad</span>
              <div>
                {PRIORITY_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={`calendar-planner__priority-pill${
                      manualEditor.priority === option.value ? ' is-active' : ''
                    }`}
                    onClick={() => handleManualEditorChange('priority', option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="calendar-planner__field">
              <span>Notas rápidas</span>
              <textarea
                value={manualEditor.notes}
                onChange={(event) => handleManualEditorChange('notes', event.target.value)}
                placeholder="Referencia de cliente, contacto, pendientes o instrucciones."
              />
            </label>
            <footer className="calendar-planner__footer">
              {manualEditorStatus ? (
                <span className="calendar-planner__status">{manualEditorStatus}</span>
              ) : null}
              <div className="calendar-planner__actions">
                {manualEditor.mode === 'edit' ? (
                  <button
                    type="button"
                    className="calendar-planner__delete"
                    onClick={() => handleManualEventDelete(manualEditor.eventId)}
                  >
                    Eliminar
                  </button>
                ) : null}
                <button type="button" onClick={closeManualEditor}>
                  Cancelar
                </button>
                <button type="submit">Guardar</button>
              </div>
            </footer>
          </form>
        </div>
      ) : null}
      {contextMenu.visible ? (
        <div
          className={`calendar-context-menu calendar-context-menu--${contextMenu.kind || 'event'}`}
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {contextMenu.kind === 'day' ? (
            <>
              <strong className="calendar-context-menu__title">
                {contextMenuDateLabel || 'Acciones del dia'}
              </strong>
              <span className="calendar-context-menu__subtitle">
                Gestiona operaciones sin salir de esta vista.
              </span>
              <button type="button" className="calendar-context-menu__option" onClick={handleContextMenuCreateManual}>
                <span className="calendar-context-menu__icon" aria-hidden="true">＋</span>
                Registrar operación manual
              </button>
              <button type="button" className="calendar-context-menu__option" onClick={handleContextMenuOpenInspector}>
                <span className="calendar-context-menu__icon" aria-hidden="true">👁</span>
                Abrir detalle del dia
              </button>
            </>
          ) : (
            <>
              <strong className="calendar-context-menu__title">Actualizar estado</strong>
              <span className="calendar-context-menu__subtitle">
                Selecciona un nuevo estado para este periodo.
              </span>
              {STATUS_OPTIONS.map((option) => {
                const normalizedOption = normalizeStatus(option.value);
                const isActive = normalizedOption === contextMenuStatus;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`calendar-context-menu__option calendar-context-menu__option--${option.value}${
                      isActive ? ' calendar-context-menu__option--selected' : ''
                    }`}
                    disabled={contextMenu.kind === 'unit-event' ? updatingRangeStatus : false}
                    aria-pressed={isActive}
                    onClick={(event) => handleStatusUpdate(option.value, event)}
                  >
                    <span
                      className={`calendar-context-menu__dot calendar-context-menu__dot--${option.value}`}
                      aria-hidden="true"
                    />
                    {option.label}
                  </button>
                );
              })}
              {contextMenu.kind === 'unit-event' && updatingRangeStatus ? (
                <span className="calendar-context-menu__status">Guardando…</span>
              ) : null}
              <div className="calendar-context-menu__separator" />
              <button type="button" className="calendar-context-menu__option" onClick={handleContextMenuOpenInspector}>
                Ver detalle del dia
              </button>
              {contextMenu.kind === 'manual-event' ? (
                <>
                  <button type="button" className="calendar-context-menu__option" onClick={handleContextMenuEditManual}>
                    Editar registro
                  </button>
                  <button
                    type="button"
                    className="calendar-context-menu__option calendar-context-menu__option--danger"
                    onClick={handleContextMenuDeleteManual}
                  >
                    Eliminar registro
                  </button>
                </>
              ) : (
                <button type="button" className="calendar-context-menu__option" onClick={handleContextMenuPromoteToManual}>
                  Duplicar como registro manual
                </button>
              )}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
